import { supabase } from './supabaseClient';

/**
 * Load all users (admin/sales_manager only)
 */
export async function loadUsers() {
  if (!supabase) return [];
  
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('❌ Error loading users:', err);
    throw err;
  }
}

/**
 * Create new user with secure credentials
 */
export async function createUser(userData) {
  if (!supabase) return null;
  
  try {
    // Generate secure random password
    const tempPassword = generateSecurePassword();
    
    const { data, error } = await supabase
      .from('users')
      .insert([{
        name: userData.name,
        email: userData.email,
        password: tempPassword, // Note: Should be hashed in production
        role: userData.role,
        phone: userData.phone || null,
        balance: userData.balance || 0,
        commission_percentage: userData.commission_percentage || null,
      }])
      .select()
      .single();
    
    if (error) throw error;
    
    // Return user with temporary password (for sharing with user)
    return { ...data, tempPassword };
  } catch (err) {
    console.error('❌ Error creating user:', err);
    throw err;
  }
}

/**
 * Update user information
 */
export async function updateUser(userId, updates) {
  if (!supabase) return null;
  
  try {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('❌ Error updating user:', err);
    throw err;
  }
}

/**
 * Delete user
 */
export async function deleteUser(userId) {
  if (!supabase) return false;
  
  try {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', userId);
    
    if (error) throw error;
    return true;
  } catch (err) {
    console.error('❌ Error deleting user:', err);
    throw err;
  }
}

/**
 * Authenticate user
 */
export async function authenticateUser(email, password) {
  if (!supabase) return null;
  
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .eq('password', password)
      .single();
    
    if (error) return null;
    
    // Update last_login and set online
    await supabase
      .from('users')
      .update({ 
        last_login: new Date().toISOString(),
        is_online: true,
        last_active: new Date().toISOString(),
      })
      .eq('id', data.id);
    
    return data;
  } catch (err) {
    console.error('❌ Error authenticating user:', err);
    return null;
  }
}

/**
 * Set user offline (on logout)
 */
export async function setUserOffline(userId) {
  if (!supabase) return;
  
  try {
    await supabase
      .from('users')
      .update({ 
        is_online: false,
        last_active: new Date().toISOString(),
      })
      .eq('id', userId);
  } catch (err) {
    console.error('⚠️ Failed to set user offline:', err);
  }
}

/**
 * Generate secure random password
 */
function generateSecurePassword(length = 12) {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
}
